import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Button, Dropdown, Toggle, Loading, Table } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import {
  getAllPaymentMethods,
  getCompanyPaymentMethods,
  createCompanyPaymentMethod,
  updateCompanyPaymentMethod,
  findPaymentSettings,
  updatePaymentSettings,
} from '../actions';
import { setBranchId } from '../../../auth/actions';
import AddPaymentMethodModal from './AddPaymentMethodModal';

const PaymentsSettings = memo(
  ({
    auth: { companyId, branchList, branchId },
    paymentMethodsState: { paymentMethodsData, companyPaymentMethodsData, paymentSettingsData },
    showAlert,
    getAllPaymentMethods,
    getCompanyPaymentMethods,
    createCompanyPaymentMethod,
    updateCompanyPaymentMethod,
    findPaymentSettings,
    setBranchId,
    updatePaymentSettings,
  }) => {
    const originalDataRef = useRef({});
    const [paymentsSettingsForm, setPaymentsSettingsForm] = useState({
      'is-opening-cashier-balance-count-popup-enabled': false,
      'is-closing-cashier-balance-count-popup-enabled': false,
    });
    const [isAddPaymentMethodModalOpen, setAddPaymentMethodModalOpen] = useState(false);
    const [editingCompanyPaymentMethod, setEditingCompanyPaymentMethod] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
      getAllPaymentMethods();
      getCompanyPaymentMethods();
      if (branchId) {
        findPaymentSettings(branchId);
      }
    }, []);

    useEffect(() => {
      if (branchId) {
        findPaymentSettings(branchId);
      }
    }, [branchId]);

    useEffect(() => {
      if (
        Array.isArray(paymentSettingsData) &&
        paymentSettingsData.length > 0
      ) {
        const nextForm = {
          'is-opening-cashier-balance-count-popup-enabled': false,
          'is-closing-cashier-balance-count-popup-enabled': false,
        };
        for (const item of paymentSettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setPaymentsSettingsForm(nextForm);
      }
    }, [paymentSettingsData]);

    const handleChangeBranch = (value) => {
      setBranchId(value);
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(paymentsSettingsForm).forEach((key) => {
        const currentVal = paymentsSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const paymentSettings = {
        paymentSettingsData: changedFields,
        branchId: branchId,
      };
      const result = await updatePaymentSettings(paymentSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Payment settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update payment settings!',
        });
      }
      setIsLoading(false);
      findPaymentSettings(branchId);
    };

    const onClickCancel = () => {
      navigate('/');
    };

    const handleAddPaymentMethod = () => {
      setEditingCompanyPaymentMethod(null);
      setAddPaymentMethodModalOpen(true);
    };

    const handleSaveCompanyPaymentMethod = async (companyPaymentMethodData) => {
      if (editingCompanyPaymentMethod) {
        const result = await updateCompanyPaymentMethod(companyPaymentMethodData, editingCompanyPaymentMethod.id);
        if (result) {
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Company payment method updated successfully!',
          });
          getCompanyPaymentMethods();
        } else {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: 'Failed to update company payment method!',
          });
        }
      } else {
        const result = await createCompanyPaymentMethod(companyPaymentMethodData);
        if (result) {
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Company payment method created successfully!',
          });
          getCompanyPaymentMethods();
        } else {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: 'Failed to create company payment method!',
          });
        }
      }
    };

    const handleUpdateCompanyPaymentMethod = (companyPaymentMethodId) => {
      const companyPaymentMethod = companyPaymentMethodsData?.find((p) => p.id === companyPaymentMethodId);
      if (companyPaymentMethod) {
        setEditingCompanyPaymentMethod({
          id: companyPaymentMethod.id,
          name: companyPaymentMethod.paymentMethodName,
          isNeedToManageAccount: companyPaymentMethod.isNeedToManageAccount,
          paymentMethodId: companyPaymentMethod.paymentMethodId,
        });
        setAddPaymentMethodModalOpen(true);
      }
    };

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setPaymentsSettingsForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const PaymentMethodsTable = () => {
      const columns = [
        { id: 'paymentMethodName', label: 'Payment Method', minWidth: 100 },
        { id: 'isNeedToManageAccount', label: 'Need to Manage Account', minWidth: 100 },
        {
          id: 'options',
          minWidth: 20,
          options: ['Update'],
          table: 'company-payment-method',
        },
      ];

      const tableRows = (companyPaymentMethodsData || []).map((companyPaymentMethod) => ({
        id: companyPaymentMethod.id,
        paymentMethodName: companyPaymentMethod.paymentMethodName || '',
        isNeedToManageAccount: companyPaymentMethod.isNeedToManageAccount ? 'Yes' : 'No',
      }));

      const handleUpdateClick = (id) => handleUpdateCompanyPaymentMethod(id);

      const tableColumns = columns.map((col) =>
        col.id === 'options'
          ? { ...col, customHandlers: { onUpdate: handleUpdateClick } }
          : col
      );

      return (
        <Table
          columns={tableColumns}
          rows={tableRows}
          disableInternalPagination={false}
        />
      );
    };

    const branchOptions = (branchList || []).map((b) => ({ value: b.id, label: b.name }));

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Payments Settings</h1>
            <p className={styles.breadcrumb}>Settings / Payments</p>
          </div>
          {branchList && branchList.length > 0 && branchId && (
            <div className={styles.branchSelectContainer}>
              <Dropdown
                id="branch-select"
                label="Branch"
                value={branchId}
                onChange={handleChangeBranch}
                options={branchOptions}
                placeholder="Select branch"
              />
            </div>
          )}
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.topicLabel}>Payment Methods</h2>
                      <p className={styles.topicCaption}>
                        Manage your company's payment methods here
                      </p>
                    </div>
                    <Button type="button" onClick={handleAddPaymentMethod}>
                      Add Payment Method
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <PaymentMethodsTable />
                </div>

                <div className={styles.line} />

                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>
                    Payment Method Configurations
                  </h2>
                  <p className={styles.topicCaption}>
                    Here you can change the payment method configurations
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={paymentsSettingsForm['is-opening-cashier-balance-count-popup-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-opening-cashier-balance-count-popup-enabled"
                    label="Opening Cashier Balance Count Popup Enable"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={paymentsSettingsForm['is-closing-cashier-balance-count-popup-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-closing-cashier-balance-count-popup-enabled"
                    label="Closing Cashier Balance Count Popup Enable"
                    labelPlacement="start"
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
        <AddPaymentMethodModal
          open={isAddPaymentMethodModalOpen}
          setOpen={setAddPaymentMethodModalOpen}
          onSave={handleSaveCompanyPaymentMethod}
          editingCompanyPaymentMethod={editingCompanyPaymentMethod}
          paymentMethods={
            editingCompanyPaymentMethod
              ? paymentMethodsData
              : (paymentMethodsData || []).filter(
                  (paymentMethod) =>
                    !(companyPaymentMethodsData || []).some(
                      (companyPaymentMethod) =>
                        companyPaymentMethod.paymentMethodId === paymentMethod.id
                    )
                )
          }
        />
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  paymentMethodsState: state.paymentMethodsState,
});

export default connect(mapStateToProps, {
  showAlert,
  getAllPaymentMethods,
  getCompanyPaymentMethods,
  createCompanyPaymentMethod,
  updateCompanyPaymentMethod,
  findPaymentSettings,
  setBranchId,
  updatePaymentSettings,
})(PaymentsSettings);
